import { AiAgentJobRequest } from '@modeso/aumico-lib-ai-agents-fe';
import { AumicoSections } from '../helpers/datev-ai.adapter';
import { TaxonomyDefinitions } from '../helpers/datev.excel.helper';

/**
 * Delegate that bundles every AI-fixer-related property that flows between
 * the parent page (FinancialStatementImportBalanceSheetComponent) and the
 * child form page (FinancialStatementImportFormPage).
 *
 * Replaces seven individual `@Input()`s and two `@Output()`s with a single
 * `[aiDelegate]` binding, following the **delegate pattern**: the parent
 * creates the object and the child reads state + calls callbacks on it.
 *
 * Usage (parent template):
 *   `[aiDelegate]="aiDelegate"`
 *
 * The parent rebuilds this object (new reference) whenever any AI state
 * changes so that Angular's `ngOnChanges` fires in the child.
 */
export interface AiFixerDelegate {
    // ── State (read-only for the child) ────────────────────────────────

    /** Whether the AI fixer panel should be visible. */
    readonly showPanel: boolean;

    /** The job request to send to the AI agent (null ⇒ nothing to run). */
    readonly jobRequest: AiAgentJobRequest | null;

    /** Firebase ID token used for WebSocket authentication. */
    readonly firebaseToken: string;

    /** Original sections before the AI fix — used for diff highlighting. */
    readonly originalSections: AumicoSections | null;

    /** Fixed sections returned by the AI agent (null until accepted). */
    readonly fixedSections: AumicoSections | null;

    /** Rebuilt TaxonomyDefinitions after the user accepted the fix. */
    readonly fixedTaxonomy: TaxonomyDefinitions | null;

    // ── Callbacks (replace @Output EventEmitters) ──────────────────────

    /** Called when the user accepts the AI fix result. */
    onAccepted(output: Record<string, unknown>): void;

    /** Called when the user rejects or cancels the AI fix. */
    onRejected(): void;
}
